import { useEffect, useRef } from 'react';
import { Badge, Stack, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDeviceDesktopDown } from '@tabler/icons-react';
import type { Participant } from 'livekit-client';
import { useTranslation } from 'react-i18next';

import { formatDuration } from './call-duration';
import { participantName } from './call/participant-name';
import { useCloudRecording, useCloudRecordingStopper, useSecondsSince } from './cloud-recording';
import { localRecordingStartedAt, useLocalRecorders } from './local-recording';
import classes from './recording-indicator.module.css';
import { i18n } from '@/i18n';

export function RecordingDot({ tone = 'filled' }: Readonly<{ tone?: 'filled' | 'light' }>) {
    return <span className={classes.dot} data-tone={tone} />;
}

export function RecordingStatus() {
    useLocalRecordersNotifications();
    return (
        <>
            <CloudRecordingBadge />
            <LocalRecordersBadge />
        </>
    );
}

function useLocalRecordersNotifications() {
    const recorders = useLocalRecorders();
    const announcedRef = useRef(new Set<string>());

    useEffect(() => {
        for (const participant of recorders) {
            if (participant.isLocal || announcedRef.current.has(participant.identity)) continue;
            notifications.show({
                id: `local-recording-other-${participant.identity}`,
                title: i18n.t('conference:recording.notify.localOther.title', {
                    name: participantName(participant.name, participant.identity),
                }),
                message: i18n.t('conference:recording.notify.localOther.message'),
            });
        }
        announcedRef.current = new Set(recorders.map((participant) => participant.identity));
    }, [recorders]);
}

function CloudRecordingBadge() {
    const { t } = useTranslation('conference');
    const { isRecording, isSaving, info } = useCloudRecording();
    const stopper = useCloudRecordingStopper(info);

    if (isSaving) {
        return (
            <Badge className={classes.badge} color="gray" data-testid="cloud-recording-saving" variant="light">
                {t('recording.saving')}
            </Badge>
        );
    }
    if (!isRecording) return null;

    const stopperName = stopper?.isLocal ? t('recording.cloud.you') : (stopper?.name ?? '');
    const stopHint = info?.transcribe
        ? t('recording.cloud.tooltipTranscript', { name: stopperName })
        : t('recording.cloud.tooltip', { name: stopperName });
    const tooltip = info?.startedBy
        ? `${stopHint} ${t('recording.cloud.tooltipStartedBy', { name: info.startedBy.name })}`
        : stopHint;

    return (
        <Tooltip multiline label={tooltip} maw="var(--spacing-80)">
            <Badge
                className={classes.badge}
                color="danger"
                data-testid="cloud-recording-badge"
                leftSection={<RecordingDot />}
                variant="filled"
            >
                {t('recording.badge')}
            </Badge>
        </Tooltip>
    );
}

export function localRecordersLabel(names: string[]): string {
    const [first = '', second = ''] = names;
    if (names.length <= 1) return first;
    if (names.length === 2) return i18n.t('conference:recording.local.pair', { first, second });
    return i18n.t('conference:recording.local.more', { first, count: names.length - 1 });
}

function LocalRecordersBadge() {
    const { t } = useTranslation('conference');
    const recorders = useLocalRecorders().toSorted((a, b) => Number(b.isLocal) - Number(a.isLocal));
    if (recorders.length === 0) return null;

    const names = recorders.map((participant) =>
        participant.isLocal ? t('recording.local.you') : participantName(participant.name, participant.identity)
    );

    return (
        <Tooltip
            label={
                <Stack gap={0}>
                    <Text inherit>{t('recording.local.count', { names: localRecordersLabel(names) })}</Text>
                    {recorders.map((participant, index) => (
                        <RecorderEntry key={participant.identity} name={names[index] ?? ''} participant={participant} />
                    ))}
                </Stack>
            }
        >
            <Badge
                className={classes.badge}
                color="red"
                data-testid="local-recording-badge"
                leftSection={<IconDeviceDesktopDown size={12} />}
                variant="light"
            >
                {recorders.length}
            </Badge>
        </Tooltip>
    );
}

function RecorderEntry({ participant, name }: Readonly<{ participant: Participant; name: string }>) {
    const { t } = useTranslation('conference');
    const seconds = useSecondsSince(localRecordingStartedAt(participant.attributes) || undefined);
    return (
        <Text inherit className={classes.time}>
            {t('recording.local.entry', { name, time: formatDuration(seconds) })}
        </Text>
    );
}
